import { captureMessage } from '@sentry/react'
import { useQuery } from '@tanstack/react-query'
import { usePostHog } from 'posthog-js/react'
import React, { useContext, useEffect, useMemo } from 'react'
import { useParams } from 'react-router'

import type { OfferDataResponseApiType } from 'app/api/api.schemas'
import { usePublicApi } from 'app/hooks/useApi'
import { isPlaygroundMode, playgroundAwareQueryRetryPolicy } from 'app/playground/mode'
import { PARTNER_GROUP_TYPE } from 'app/tracking/events'
import { isOfferKizzo, isPartnerKizzo } from 'app/utils/partner'
import { isPartnerNotEnabledError } from 'app/utils/partnerNotEnabledError'

import { SharedContext } from './sharedContext'

// Hook to extract partner ID from various sources
export function usePartnerId(offerData?: any): string | null {
    const { partnerId: urlPartnerId } = useParams()

    return useMemo(() => {
        // Priority 1: URL params (prequal flow)
        if (urlPartnerId) return urlPartnerId

        // Priority 2: Offer data (offer flow)
        if (offerData?.data?.partner_id) return offerData.data.partner_id

        return null
    }, [urlPartnerId, offerData])
}

interface SharedProviderProps {
    children: React.ReactNode
    partnerId?: string | null // Simple partner ID prop
    /** Offer data for origination flow - used to determine is_kizzo for logo switching */
    offerData?: { data?: OfferDataResponseApiType } | null
}

export function SharedProvider({ children, partnerId, offerData }: SharedProviderProps) {
    const { getPartnerConfiguration } = usePublicApi()
    const { partnerId: urlPartnerId } = useParams()
    const isPlayground = isPlaygroundMode()
    const posthog = usePostHog()

    const {
        data: partnerConfig,
        isLoading: partnerConfigLoading,
        error: partnerConfigError
    } = useQuery({
        enabled: !!partnerId,
        queryKey: ['partnerConfiguration', partnerId],
        queryFn: () => getPartnerConfiguration({ partner_id: partnerId! }),
        retryDelay: 1000,
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 10 * 60 * 1000, // 10 minutes
        ...playgroundAwareQueryRetryPolicy(isPlayground)
    })

    // Log additional context when an error occurs
    useEffect(() => {
        if (partnerConfigError && !isPartnerNotEnabledError(partnerConfigError)) {
            let partnerIdSource = 'unknown'
            if (urlPartnerId) {
                partnerIdSource = 'URL'
            } else if (partnerId) {
                partnerIdSource = 'offerData'
            }

            captureMessage('Error in partner configuration query', {
                level: 'info',
                extra: {
                    error: partnerConfigError,
                    partnerId,
                    urlPartnerId,
                    partnerIdSource
                }
            })
        }
    }, [partnerConfigError, partnerId, urlPartnerId])

    // Origination: isOfferKizzo(offer). Prequal: partner config flag, else Kizzo partner id (before config loads).
    const isKizzoBranded =
        offerData?.data !== undefined
            ? isOfferKizzo(offerData.data)
            : partnerConfig?.is_kizzo_prequal_enabled === true || (partnerId != null && isPartnerKizzo(partnerId))
    const isSuperPrimeOffer = offerData?.data?.is_super_prime === true

    // Associate this session's PostHog events with the partner group so funnels can be
    // broken down by partner
    useEffect(() => {
        if (posthog && partnerId) {
            posthog.group(PARTNER_GROUP_TYPE, partnerId, {
                partner_name: partnerConfig?.name,
                is_kizzo_branded: isKizzoBranded
            })
        }
    }, [posthog, partnerId, partnerConfig?.name, isKizzoBranded])

    const contextValue = useMemo(
        () => ({
            partnerConfig,
            partnerConfigLoading,
            partnerConfigError,
            isKizzoBranded,
            isSuperPrimeOffer,
            offerData
        }),
        [partnerConfig, partnerConfigLoading, partnerConfigError, isKizzoBranded, isSuperPrimeOffer, offerData]
    )

    return <SharedContext.Provider value={contextValue}>{children}</SharedContext.Provider>
}

export function useSharedContext() {
    const context = useContext(SharedContext)
    if (context === undefined) {
        throw new Error('useSharedContext must be used within a SharedProvider')
    }
    return context
}
